// ══════════════════════════════════════════════════════════════
// COMPASS – Process State Model
// ══════════════════════════════════════════════════════════════
import { KycProcessDto } from '../../api/models/kyc-process-dto';
import { KycConsentEvidenceDto } from '../../api/models/kyc-consent-evidence-dto';
import { KycProcessListDto } from '../../api/models/kyc-process-list-dto';
import { KycPersonFormDto } from '../../api/models/kyc-person-form-dto';
import { KycCompanyFormDto } from '../../api/models/kyc-company-form-dto';
import { KycChangeRequestWithItemsDto } from '../../api/models/kyc-change-request-with-items-dto';
import { KycProcessApprovalStateDto } from '../../api/models/kyc-process-approval-state-dto';
import { KycProcessApprovalDto } from '../../api/models/kyc-process-approval-dto';
import { KycWorkflowEventDto } from '../../api/models/kyc-workflow-event-dto';
import { KycProcessParticipantDto } from '../../api/models/kyc-process-participant-dto';
import { ProcessObservationKind } from '../../models/process-observation.model';
import { ClientRiskMatrixStateResponseDto } from '../../api/models/client-risk-matrix-state-response-dto';
import { ClientRiskMatrixAssessResponseDto } from '../../api/models/client-risk-matrix-assess-response-dto';
import { ClientRiskMatrixMeasuresResponseDto } from '../../api/models/client-risk-matrix-measures-response-dto';
import { GroupedScreeningsDto } from '../../api/models/grouped-screenings-dto';
import { WorldCheckOneCaseDto } from '../../api/models/world-check-one-case-dto';
import { MissingTargetDto } from '../../api/models/missing-target-dto';
import { KycProcessStatisticsDto } from '../../api/models/kyc-process-statistics-dto';
import { KycReportDto } from '../../api/models/kyc-report-dto';
import { KycComplianceReportDto } from '../../api/models/kyc-compliance-report-dto';
import { EntrustSignatureStatusResponseDto } from '../../api/models/entrust-signature-status-response-dto';
import { EntrustSignatureEvidenceFolderDto } from '../../api/models/entrust-signature-evidence-folder-dto';
import { KycFormSnapshotSummaryDto } from '../../api/models/kyc-form-snapshot-summary-dto';
import { KycFormSnapshotDto } from '../../api/models/kyc-form-snapshot-dto';

export interface KycProcessListRow extends KycProcessListDto {
  companyName?: string;
}

/** One of the Compliance Officer's observations of the selected process (US-005, US-009). */
export interface ProcessObservationState {
  /** null when there is none. */
  text: string | null;
  loading: boolean;
  /** Loaded for the selected process, so the field can be edited without overwriting what it never showed. */
  loaded: boolean;
  loadFailed: boolean;
  saving: boolean;
  /** Bumped by every load and save answer, so the card shows the stored value even when it is unchanged. */
  revision: number;
}

export const initialObservationState: ProcessObservationState = {
  text: null,
  loading: false,
  loaded: false,
  loadFailed: false,
  saving: false,
  revision: 0,
};

/** The full compliance KYC report (US-003 on US-002), Compliance Officer only. */
export interface ComplianceReportState {
  /** The current one; null when there is none (yet). Kept while a newer one is awaited. */
  report: KycComplianceReportDto | null;
  /** The first load is on its way (re-checks show no spinner). */
  loading: boolean;
  /** Answered for the selected process (a report or none). */
  loaded: boolean;
  /** The last load the user saw failed: the backend's message (translated), shown with a retry. */
  error: string | null;
  /** The analysis changed since `report` was generated: a newer one is on its way (the backend regenerates it). */
  updating: boolean;
  /** generatedAt of the report shown when it went out of date: a different one is the update. */
  updatingFrom: string | null;
  /** The re-checks ran out with none there yet: the page stops saying it is on its way and offers a retry. */
  waitEnded: boolean;
}

export const initialComplianceReportState: ComplianceReportState = {
  report: null,
  loading: false,
  loaded: false,
  error: null,
  updating: false,
  updatingFrom: null,
  waitEnded: false,
};

export interface ProcessState {
  processes: KycProcessListRow[];
  selectedProcess: KycProcessDto | null;
  individualForm: KycPersonFormDto | null;
  companyForm: KycCompanyFormDto | null;
  loading: boolean;
  /** The process list request only: `loading` is shared with the open process and its forms. */
  processesLoading: boolean;
  formSaving: boolean;
  error: string | null;
  changeRequests: KycChangeRequestWithItemsDto[];
  changeRequestsLoading: boolean;
  statusTransitioning: boolean;
  reopening: boolean;
  clientReopening: boolean;
  formVersions: KycFormSnapshotSummaryDto[];
  formVersionsLoading: boolean;
  selectedFormVersion: KycFormSnapshotDto | null;
  formVersionDetailLoading: boolean;
  approvalState: KycProcessApprovalStateDto | null;
  approvals: KycProcessApprovalDto[];
  approvalLoading: boolean;
  /** The approvals list (history) on its own: approvalLoading is shared with the approval state. */
  approvalsLoading: boolean;
  approvalsError: string | null;
  approvalDeciding: boolean;
  timeline: KycWorkflowEventDto[];
  timelineLoading: boolean;
  participants: KycProcessParticipantDto[];
  participantsLoading: boolean;
  riskMatrixState: ClientRiskMatrixStateResponseDto | null;
  riskMatrixLoading: boolean;
  riskMatrixAssessing: boolean;
  assessResult: ClientRiskMatrixAssessResponseDto | null;
  riskMeasures: ClientRiskMatrixMeasuresResponseDto | null;
  riskMeasuresLoading: boolean;
  riskMeasuresSaving: boolean;
  /** US-011: the Compliance Officer's results on their way to the backend. */
  riskResultsSaving: boolean;
  /** US-005, US-009: the Compliance Officer's observations of the selected process, by kind. */
  observations: Record<ProcessObservationKind, ProcessObservationState>;
  screenings: GroupedScreeningsDto[];
  screeningsLoading: boolean;
  /** A screenings load has finished (successfully or not) for the selected process. */
  screeningsLoaded: boolean;
  screeningsError: string | null;
  screeningsRunning: boolean;
  worldcheckCases: WorldCheckOneCaseDto[];
  worldcheckCasesLoading: boolean;
  worldcheckCasesEmpty: boolean;
  worldcheckCasesError: string | null;
  /** World-Check status of the whole process: COMPLETED, FAILED, or null while still running. */
  worldcheckFinalStatus: string | null;
  /** People/companies whose World-Check screening failed (the backend retries them). */
  worldcheckMissingTargets: MissingTargetDto[];
  statistics: KycProcessStatisticsDto | null;
  statisticsLoading: boolean;
  kycReport: KycReportDto | null;
  kycReportLoading: boolean;
  /** The full compliance report (US-003), Compliance Officer only: the current one, kept while a newer is awaited. */
  complianceReport: ComplianceReportState;
  signatureStatus: EntrustSignatureStatusResponseDto | null;
  signatureStatusLoading: boolean;
  /** The signature status of the selected process has been asked for and answered (a status, none,
   *  or a failed call), so a null signatureStatus no longer means "not asked yet". */
  signatureStatusLoaded: boolean;
  /** The last signature-status call failed, so "none" is not known (US-025: no delete on a guess). */
  signatureStatusFailed: boolean;
  signatureRequesting: boolean;
  signatureSkipping: boolean;
  evidenceFolders: EntrustSignatureEvidenceFolderDto[];
  evidenceFoldersLoading: boolean;
  /** US-015: the client consent evidence of the selected process; null when there is none (or not loaded). */
  consentEvidence: KycConsentEvidenceDto | null;
  consentEvidenceLoaded: boolean;
  /** The backend does not show this user the evidence (403, by role): whether there is one is unknown. */
  consentEvidenceHidden: boolean;
  consentEvidenceLoadFailed: boolean;
  consentEvidenceUploading: boolean;
}

export const initialProcessState: ProcessState = {
  processes: [],
  selectedProcess: null,
  individualForm: null,
  companyForm: null,
  loading: false,
  processesLoading: false,
  formSaving: false,
  error: null,
  changeRequests: [],
  changeRequestsLoading: false,
  statusTransitioning: false,
  reopening: false,
  clientReopening: false,
  formVersions: [],
  formVersionsLoading: false,
  selectedFormVersion: null,
  formVersionDetailLoading: false,
  approvalState: null,
  approvals: [],
  approvalLoading: false,
  approvalsLoading: false,
  approvalsError: null,
  approvalDeciding: false,
  timeline: [],
  timelineLoading: false,
  participants: [],
  participantsLoading: false,
  riskMatrixState: null,
  riskMatrixLoading: false,
  riskMatrixAssessing: false,
  assessResult: null,
  riskMeasures: null,
  riskMeasuresLoading: false,
  riskMeasuresSaving: false,
  riskResultsSaving: false,
  observations: {
    sourceOfFunds: initialObservationState,
    geographicRiskAnalysis: initialObservationState,
    finalObservations: initialObservationState,
  },
  screenings: [],
  screeningsLoading: false,
  screeningsLoaded: false,
  screeningsError: null,
  screeningsRunning: false,
  worldcheckCases: [],
  worldcheckCasesLoading: false,
  worldcheckCasesEmpty: false,
  worldcheckCasesError: null,
  worldcheckFinalStatus: null,
  worldcheckMissingTargets: [],
  statistics: null,
  statisticsLoading: false,
  kycReport: null,
  kycReportLoading: false,
  complianceReport: initialComplianceReportState,
  signatureStatus: null,
  signatureStatusLoading: false,
  signatureStatusLoaded: false,
  signatureStatusFailed: false,
  signatureRequesting: false,
  signatureSkipping: false,
  evidenceFolders: [],
  evidenceFoldersLoading: false,
  consentEvidence: null,
  consentEvidenceLoaded: false,
  consentEvidenceHidden: false,
  consentEvidenceLoadFailed: false,
  consentEvidenceUploading: false,
};
